// Recipient-list rules for the usage reports — ONE copy, shared by the company
// page and the platform page.
//
// The address rule itself is NOT defined here: it is validateEmail() in
// lib/validation.js, which mirrors backend/auth/schemas.py::_EMAIL_RE. The
// platform page had grown a second, weaker regex of its own
// (/^[^\s@]+@[^\s@]+\.[^\s@]+$/), so the two pages disagreed about the same
// address and neither matched what the API would accept. Anything that needs to
// know whether an address is valid asks lib/validation.
//
// These functions are pure and hold no state, so the two pages can keep their
// different save models (the company report persists as you edit, a platform
// schedule has an explicit Save) while sharing the rules exactly.
import { validateEmail } from "@/lib/validation";

/** The stored form of a typed address: trimmed and lower-cased.
 *
 * Lower-casing is what makes "Anil@x.com" and "anil@x.com" one mailbox rather
 * than two entries that would each be mailed. */
export function normalizeRecipient(raw) {
  return (raw || "").trim().toLowerCase();
}

/** Error message for adding `raw` to `list`, or "" when it is acceptable.
 *
 * A blank or whitespace-only entry is an ERROR here, not something to quietly
 * skip: the user typed into the box and pressed Add, so silently doing nothing
 * looks like the control is broken. (Whether an EMPTY list is allowed is a
 * different question — that depends on the automatic-report toggle.) */
export function validateRecipient(raw, list = []) {
  const value = (raw || "").trim();
  if (!value) return "Enter an email address.";
  const invalid = validateEmail(value);
  if (invalid) return invalid;
  if (list.some((r) => normalizeRecipient(r) === normalizeRecipient(value))) {
    return "That email is already in the list.";
  }
  return "";
}

/** Fold a half-typed draft into the list at save time.
 *
 * Returns { list, error }. An address typed into the box but never "added" used
 * to be dropped on the floor by Save, so a schedule could be stored with no
 * recipients by someone who had plainly just entered one; and the blank case
 * has to stay silent, because an empty box at save time is the normal state. */
export function commitPending(list, pending) {
  const value = (pending || "").trim();
  if (!value) return { list, error: "" };
  const error = validateRecipient(value, list);
  if (error) return { list, error };
  return { list: [...list, normalizeRecipient(value)], error: "" };
}

/** Error message for saving `list`, or "" when the configuration is storable.
 *
 * AT LEAST ONE RECIPIENT IS ALWAYS REQUIRED, in every state of the page. It used
 * to depend on the automatic-report switch, which conflated two different
 * questions: the switch says whether the scheduler sends, not whether the
 * configuration names anybody. A report with no recipients cannot be delivered
 * by any route — scheduled or Send now — so it is not a storable configuration.
 *
 * Mirrors reporting.usage_report.clean_recipients, which is the control. */
export function validateRecipientList(list) {
  const clean = (list || []).map(normalizeRecipient).filter(Boolean);
  if (clean.length === 0) return "At least one valid recipient email is required.";
  const bad = clean.find((r) => validateEmail(r));
  return bad ? `"${bad}" is not a valid email address.` : "";
}
